/**
 * useClientOrgFilter — BLU-3xx Phase 47.01.2-B-03
 *
 * Encapsulates the cross-org per-client filter logic for the Inventory page,
 * mirroring the BLU-389 pattern from `OrdersPage.tsx:93–190` (the canonical
 * reference). Default state is "All clients"; the filter narrows the table
 * to a single client when chosen.
 *
 * Visibility rule: only render the dropdown when the shared-locations feature
 * flag is on AND the current data set actually contains rows from more than
 * one organisation. Single-tenant deployments and shared-location pages with
 * only the operator's own stock should see no chrome change.
 *
 * The hook fetches connected-client names from the location-scoped endpoint
 * `locations.listConnectedClientsAtLocation` (sub-phase B-01) so that
 * Staff/Coordinator users at a single shared location see resolved client
 * names without the cross-location roster leak the older admin-only
 * `listConnectedClients` would have caused. Org ids without a resolved
 * name (e.g. data race, role gate denial) fall back to a UUID-prefix label
 * surfaced by the consumer.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useConnectedClients } from './useConnectedClients';

export const ALL_CLIENTS = '__all__';

/**
 * Default org-id accessor. Module-level (not an inline default) so its
 * identity is stable across renders and the memos below don't recompute on
 * every render for callers that don't pass one.
 */
const defaultGetOrgId = (row: RowWithOrg): string | undefined =>
  row.ownerOrganizationId;

/**
 * A row this hook can filter. Both field names are listed because the two
 * row families that use it name the column differently — inventory/receipt
 * rows expose `ownerOrganizationId`, production-tracker rows carry the raw
 * `organization_id`. Listing both also keeps this from being a TS "weak type"
 * (all-optional), which would reject any row that shares neither name and is
 * the reason a caller must go through `getOrgId` rather than a bare cast.
 */
interface RowWithOrg {
  ownerOrganizationId?: string;
  organization_id?: string | null;
}

interface UseClientOrgFilterParams<T extends RowWithOrg> {
  rows: T[];
  ownOrgId?: string;
  ownOrgName?: string;
  /** Location id used to scope the connected-clients query. */
  locationId?: string;
  /**
   * How to read a row's org id. Defaults to `ownerOrganizationId` (the
   * inventory/receipt row shape this hook was built for). Production-tracker
   * rows carry the raw column name `organization_id` instead, so they pass
   * an accessor rather than forcing every surface onto one field name.
   */
  getOrgId?: (row: T) => string | undefined;
  /**
   * Auto-clear the selection when the chosen org isn't present in `rows`.
   * Default true (the shipped behaviour for every inventory surface). Pass
   * false when a single selection spans several paged data sources and
   * "absent" doesn't mean "gone" — see the effect below.
   */
  resetWhenAbsent?: boolean;
  /**
   * BLU-1423: the client this scope opens on (the Orders page passes the
   * floor's saved default). Used until the person picks.
   *
   * Like `initialChoice`, it only applies while it names a client in the
   * switcher's roster. Before the roster loads it is used as-is, so the first
   * query already carries it; once the roster has loaded, an id outside it (a
   * revoked client), or no switcher at all, reads as All clients. The server
   * ignores a client the caller can't reach and returns every org's rows, so
   * keeping a stale id would label the whole floor as one client.
   */
  defaultOrgId?: string | null;
  /**
   * BLU-1423: what a pick belongs to (the location id). When it changes, the
   * pick is dropped and the new scope opens on its own `defaultOrgId`.
   * Omitted = a pick simply persists, as before.
   */
  scopeKey?: string;
  /**
   * BLU-1423: a pick carried in from outside the page (the Orders page URL),
   * for the first scope the page lands on only. `ALL_CLIENTS` is an explicit
   * All clients pick.
   */
  initialChoice?: string;
}

/** The person's pick and the scope it was made in. */
interface ScopedPick {
  scopeKey: string | undefined;
  value: string | undefined;
  /** From `initialChoice`: checked against the roster like a default. */
  carriedIn: boolean;
}

export interface ClientOrgFilterState<T extends RowWithOrg> {
  /** Current filter value (`ALL_CLIENTS` sentinel or an org id). */
  clientOrgFilter: string;
  setClientOrgFilter: (next: string) => void;
  /** Whether the dropdown should be rendered at all (flag + data check). */
  showClientFilter: boolean;
  /** Whether a specific client is currently selected (i.e. not ALL). */
  isFiltered: boolean;
  /** Rows after applying the client filter. */
  filteredRows: T[];
  /** Distinct org ids actually present in the un-filtered data. */
  distinctOrgsInData: string[];
  /** Resolved name for each org id (own org first, then connected clients). */
  orgIdToName: Map<string, string>;
  /** Selected client name when filtered, null otherwise. */
  selectedClientName: string | null;
  /** Loading state for the connected-clients query. */
  isLoadingClients: boolean;
  /**
   * The person's own pick in force for this scope (an org id or
   * `ALL_CLIENTS`), undefined while the scope is on its default. What a
   * caller persists (the Orders page keeps it in the URL).
   */
  pickedOrgFilter: string | undefined;
}

/**
 * BLU-3xx Phase 47.01.2-B-03: per-client filter state for the inventory page.
 *
 * @param rows - Inventory rows to filter (each must carry `ownerOrganizationId`).
 * @param ownOrgId - The caller's own org id (from `useOrganization()`).
 * @param ownOrgName - The caller's own org display name.
 * @param locationId - Optional. When omitted, the connected-clients query is
 *                     disabled and only own-org name resolution works. Pass
 *                     the currently selected location id to enable cross-org
 *                     name resolution.
 */
export function useClientOrgFilter<T extends RowWithOrg>({
  rows,
  ownOrgId,
  ownOrgName,
  locationId,
  getOrgId = defaultGetOrgId,
  resetWhenAbsent = true,
  defaultOrgId,
  scopeKey,
  initialChoice,
}: UseClientOrgFilterParams<T>): ClientOrgFilterState<T> {
  const { t } = useTranslation('inventory');
  const sharedLocationsEnabled =
    (import.meta.env.VITE_SHARED_LOCATIONS_ENABLED ?? '') === 'true';

  const [pick, setPick] = useState<ScopedPick>(() => ({
    scopeKey,
    value: initialChoice,
    carriedIn: initialChoice !== undefined,
  }));

  // Synced during render, never in an effect: an effect would let one render
  // hand the caller's queries the previous scope's pick, and fetch twice.
  // A URL pick that arrived before any location belongs to the first one.
  let currentPick = pick;
  if (pick.scopeKey !== scopeKey) {
    currentPick =
      pick.carriedIn && pick.scopeKey === undefined
        ? { ...pick, scopeKey }
        : { scopeKey, value: undefined, carriedIn: false };
    setPick(currentPick);
  }

  const setClientOrgFilter = useCallback((next: string) => {
    setPick((prev) =>
      prev.value === next && !prev.carriedIn ? prev : { scopeKey: prev.scopeKey, value: next, carriedIn: false },
    );
  }, []);

  // BLU-3xx Phase 47.01.2-B-01 + B-fix MED-6: location-scoped
  // connected-clients query via the shared `useConnectedClients` hook
  // — React Query dedupes against `ClientTargetPicker` and the
  // write-modal visibility checks. The location-scoped variant is
  // intentionally available to Coordinator/Staff users (admin-only
  // original would block them from seeing client names), with
  // cross-location roster leakage prevented at the service layer.
  const { clients: connectedClients, isLoading: isLoadingClients } =
    useConnectedClients({
      locationId,
      enabled: sharedLocationsEnabled,
    });

  const orgIdToName = useMemo(() => {
    const map = new Map<string, string>();
    if (ownOrgId && ownOrgName) {
      map.set(ownOrgId, t('crossOrg.picker.youSuffix', { name: ownOrgName }));
    }
    connectedClients.forEach((c) => {
      if (!map.has(c.id)) map.set(c.id, c.name);
    });
    return map;
  }, [connectedClients, ownOrgId, ownOrgName, t]);

  const distinctOrgsInData = useMemo(() => {
    const ids = new Set<string>();
    for (const row of rows) {
      const orgId = getOrgId(row);
      if (orgId) ids.add(orgId);
    }
    return Array.from(ids);
  }, [rows, getOrgId]);

  // Visibility: hide the filter for single-tenant data sets even when the
  // flag is on. The filter is daily navigation for 3PL operators — noise for
  // everyone else.
  //
  // BLU-3xx Phase 47.01.2-B-fix3.5 (BUG 5d): gate on `connectedClients.length`
  // (operator's static access at this location) rather than
  // `distinctOrgsInData.length` (live data content). Pre-fix3.5: pages where
  // the data happened to contain only one org — even a 3PL operator with
  // multiple connected clients but no own-org receipts in the current window
  // — would hide the filter. Post-fix3.5 the filter renders consistently
  // whenever the operator HAS multiple accessible orgs, so they can pre-
  // filter before any data arrives. distinctOrgsInData stays exposed for
  // callers that want the data-centric signal.
  const showClientFilter =
    sharedLocationsEnabled && connectedClients.length > 0;

  const requested = currentPick.value ?? defaultOrgId ?? ALL_CLIENTS;
  const isCarriedIn = currentPick.value === undefined ? !!defaultOrgId : currentPick.carriedIn;
  const clientOrgFilter =
    isCarriedIn &&
    requested !== ALL_CLIENTS &&
    !isLoadingClients &&
    !(showClientFilter && orgIdToName.has(requested))
      ? ALL_CLIENTS
      : requested;

  // Reset filter when the selected org disappears from the data set (e.g.
  // location switch, page change).
  //
  // Opt-out (`resetWhenAbsent: false`) exists for surfaces that page through
  // more than one data source behind a single selection — the production
  // tracker switches between three, so an absent org is routinely just "not
  // on THIS tab / THIS page" rather than "no longer reachable". There, silently
  // clearing would both destroy the operator's selection on every tab switch
  // and pre-empt the surface's own "no orders for <client> on this page"
  // empty state.
  useEffect(() => {
    if (
      resetWhenAbsent &&
      clientOrgFilter !== ALL_CLIENTS &&
      !distinctOrgsInData.includes(clientOrgFilter)
    ) {
      setClientOrgFilter(ALL_CLIENTS);
    }
  }, [clientOrgFilter, distinctOrgsInData, resetWhenAbsent, setClientOrgFilter]);

  const filteredRows = useMemo(() => {
    if (clientOrgFilter === ALL_CLIENTS) return rows;
    return rows.filter((r) => getOrgId(r) === clientOrgFilter);
  }, [rows, clientOrgFilter, getOrgId]);

  // BLU-3xx Phase 47.01.2-B-fix2 NEW-FM2: when the filter target IS
  // the caller's own org, strip the "(You)" suffix from the resolved
  // name. The banner's "You are viewing:" prefix already conveys
  // ownership; doubling it ("You are viewing: Threadheads (You) at
  // HQ") reads as a stutter. The `orgIdToName` map intentionally
  // stamps the suffix for inline-list contexts (dropdown options
  // among other-owner rows) where the disambiguation matters; we
  // only undo it for the filter-narrowed banner case.
  const selectedClientName = (() => {
    if (clientOrgFilter === ALL_CLIENTS) return null;
    if (clientOrgFilter === ownOrgId && ownOrgName) {
      return ownOrgName;
    }
    return orgIdToName.get(clientOrgFilter) ?? null;
  })();

  return {
    clientOrgFilter,
    setClientOrgFilter,
    showClientFilter,
    isFiltered: clientOrgFilter !== ALL_CLIENTS,
    filteredRows,
    distinctOrgsInData,
    orgIdToName,
    selectedClientName,
    isLoadingClients,
    pickedOrgFilter: currentPick.value,
  };
}
